import React from 'react';
import { 
  Wrench, 
  Wifi, 
  Mail, 
  Monitor, 
  Globe, 
  Database, 
  Shield, 
  Printer,
  ArrowRight
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from './ui/Link';

interface ServiceCardProps {
  icon: React.ReactNode;
  title: string;
  description?: string;
  ctaLabel: string;
  ctaHref: string;
  featured?: boolean;
  delay?: number;
}

const ServiceCard: React.FC<ServiceCardProps> = ({ icon, title, description, ctaLabel, ctaHref, featured = false, delay = 0 }) => {
  return (
    <motion.div
      className={`group relative backdrop-blur-sm rounded-xl card-spacing transition-all duration-300 overflow-hidden border flex flex-col h-full ${
        featured
          ? 'bg-gradient-to-b from-[#5865F2]/20 to-white/[0.03] border-[#5865F2]/40'
          : 'bg-white/5 hover:bg-white/10 border-white/5 hover:border-white/10'
      }`}
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.45, delay, ease: 'easeOut' }}
      whileHover={{ y: -6, scale: 1.01 }}
    >
      {/* Hover gradient effect */}
      <div className={`absolute -inset-0.5 bg-gradient-to-r from-[#5865F2] to-[#EB459E] ${featured ? 'opacity-20' : 'opacity-0 group-hover:opacity-20'} blur rounded-xl transition-opacity duration-300`}></div>
      
      <div className="relative z-10 flex flex-col h-full content-spacing">
        {featured && (
          <span className="inline-flex self-start rounded-full border border-[#9BA8FF]/40 bg-[#5865F2]/20 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-[#C7D0FF]">
            Le plus demandé
          </span>
        )}
        <div className="w-12 h-12 rounded-lg bg-[#5865F2]/10 flex items-center justify-center group-hover:bg-[#5865F2]/20 transition-all duration-300 text-[#5865F2]">
          {icon}
        </div>
        
        <h3 className="text-white text-base font-medium leading-tight">{title}</h3>
        {description && <p className="text-gray-400 text-sm">{description}</p>}
        
        <div className="mt-auto pt-4">
          <a
            href={ctaHref}
            className="w-full group-hover:bg-gradient-to-r group-hover:from-[#5865F2] group-hover:via-[#4752C4] group-hover:to-[#5865F2] group-hover:text-white text-[#5865F2] bg-gradient-to-r from-[#5865F2]/10 via-[#4752C4]/10 to-[#5865F2]/10 rounded-lg px-4 py-2 text-sm font-medium transition-all duration-300 flex items-center justify-center"
          >
            <span>{ctaLabel}</span>
            <ArrowRight size={14} className="ml-1 transform group-hover:translate-x-1 transition-transform duration-300" />
          </a>
        </div>
      </div>
    </motion.div>
  );
};

const Services: React.FC = () => {
  const services = [
    {
      icon: <Wrench size={18} />,
      title: "Dépannage logiciel",
      description: "Correction rapide des bugs, erreurs et installations bloquantes pour retrouver un poste opérationnel.",
      ctaLabel: "Résoudre ce problème",
      ctaHref: "https://wa.me/33695358625",
      featured: true,
    },
    {
      icon: <Wifi size={18} />,
      title: "Problèmes de réseau",
      description: "Stabilisation de votre connexion internet et WiFi pour éviter les coupures et pertes de productivité.",
      ctaLabel: "Corriger mon réseau",
      ctaHref: "https://wa.me/33695358625",
    },
    {
      icon: <Mail size={18} />,
      title: "Gestion des emails",
      description: "Récupération d'accès, synchronisation et configuration fiable de vos boîtes mail pro et perso.",
      ctaLabel: "Récupérer mes emails",
      ctaHref: "https://wa.me/33695358625",
    },
    {
      icon: <Monitor size={18} />,
      title: "Performance PC",
      description: "Optimisation complète pour accélérer votre PC et limiter les ralentissements au quotidien.",
      ctaLabel: "Optimiser mon PC",
      ctaHref: "https://wa.me/33695358625",
    },
    {
      icon: <Globe size={18} />,
      title: "Applications web",
      description: "Accompagnement sur M365, Google Workspace et outils cloud pour un usage fluide et sans blocage.",
      ctaLabel: "Dépanner mes apps",
      ctaHref: "https://wa.me/33695358625",
    },
    {
      icon: <Database size={18} />,
      title: "Gestion de données",
      description: "Récupération, transfert et sauvegarde de vos données avec priorité à la sécurité et la continuité.",
      ctaLabel: "Sauver mes données",
      ctaHref: "https://wa.me/33695358625",
    },
    {
      icon: <Shield size={18} />,
      title: "Sécurité informatique",
      description: "Protection proactive, suppression des menaces et durcissement de votre environnement informatique.",
      ctaLabel: "Sécuriser mon poste",
      ctaHref: "https://wa.me/33695358625",
    },
    {
      icon: <Printer size={18} />,
      title: "Support imprimantes",
      description: "Configuration et dépannage de vos imprimantes pour retrouver une impression fiable rapidement.",
      ctaLabel: "Réparer mon imprimante",
      ctaHref: "https://wa.me/33695358625",
    }
  ];

  return (
    <section id="services" className="section-spacing bg-gradient-to-b from-[#2F3136] to-[#36393F]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center section-title-spacing">
          <div className="section-subtitle-badge">
            <Wrench size={14} className="mr-1.5" />
            <span className="text-xs font-medium">Nos Services</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Des solutions <span className="text-[#5865F2]">expertes</span> pour tous vos besoins
          </h2>
          <p className="text-gray-300 max-w-xl mx-auto text-sm">
            Notre équipe de spécialistes vous accompagne pour résoudre efficacement vos problèmes informatiques.
          </p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => (
            <ServiceCard
              key={index}
              icon={service.icon}
              title={service.title}
              description={service.description}
              ctaLabel={service.ctaLabel}
              ctaHref={service.ctaHref}
              featured={service.featured}
              delay={index * 0.04}
            />
          ))}
        </div>

        <div className="mt-16 text-center">
          <Link
            href="#contact"
            className="group relative inline-flex items-center justify-center px-8 py-4 font-bold text-white transition-all duration-300 ease-in-out transform hover:scale-105 bg-gradient-to-r from-[#5865F2] via-[#4752C4] to-[#5865F2] rounded-lg shadow-lg hover:shadow-[#5865F2]/50"
          >
            <span className="relative z-10 flex items-center">
              <Wrench className="mr-2 h-5 w-5" />
              Besoin d'aide ? Contactez-nous
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-[#4752C4] via-[#5865F2] to-[#4752C4] rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </Link>
          <p className="mt-4 text-gray-400 text-sm">
            Réponse garantie dans la journée
          </p>
        </div>
      </div>
    </section>
  );
};

export default Services;